import { useState, useContext, useEffect } from "react";
import { parseCookies } from "nookies";

import { Button, Paper, TextField, Text, Box } from "../../core";
import { AuthContext } from "../../../contexts/AuthContext";
import { AppContext } from "../../../contexts/AppContext";
import { CustomSnackBar } from "../../core/Snackbar";
import { totpSetupRequest } from "../../../services/auth";

const TOTP_COOKIE = "fundamentos.totpToken";

function getTotpErrorMessage(error) {
  const status = error?.status || error?.statusCode;
  if (status === 429 || error?.message?.includes("Muitas tentativas")) {
    return (
      error?.message ||
      "Muitas tentativas. Tente novamente em alguns minutos."
    );
  }
  return "Código inválido ou expirado";
}

function normalizeTotpCode(value) {
  return value.replace(/\D/g, "").slice(0, 6);
}

export default function TotpSetup() {
  const { confirmTotpSetup, isAuthenticated, history } =
    useContext(AuthContext);
  const { setLoading } = useContext(AppContext);

  const [qrDataUri, setQrDataUri] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [statusMessage, setStatusMessage] = useState({
    severity: "error",
    message: "",
  });
  const [showSnackBar, setShowSnackBar] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      history.push("/app");
    }
  }, [history, isAuthenticated]);

  useEffect(() => {
    const { [TOTP_COOKIE]: totpToken } = parseCookies();
    if (!totpToken) {
      history.push("/");
      return;
    }

    setLoading(true);
    totpSetupRequest(totpToken)
      .then((data) => {
        if (data?.message || data?.error) {
          history.push("/");
          return;
        }
        setQrDataUri(data.qrDataUri || "");
        setManualKey(data.manualKey || "");
      })
      .catch(() => {
        history.push("/");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [history, setLoading]);

  const handleCodeChange = (event) => {
    setCode(normalizeTotpCode(event.target.value));
  };

  const handleCodePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text");
    setCode(normalizeTotpCode(pasted));
  };

  const submitForm = async (event) => {
    event.preventDefault();

    if (code.length !== 6) {
      setStatusMessage({
        severity: "warning",
        message: "Informe o código de 6 dígitos do autenticador.",
      });
      setShowSnackBar(true);
      return;
    }

    setLoading(true);
    try {
      await confirmTotpSetup(code);
      history.push("/app");
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message: getTotpErrorMessage(error),
      });
      setShowSnackBar(true);
    } finally {
      setLoading(false);
    }
  };

  const redirectToLogin = (event) => {
    event.preventDefault();
    history.push("/");
  };

  return (
    <div style={styles.container}>
      <form onSubmit={submitForm}>
        <Paper style={styles.loginBox} elevation={6}>
          <div style={styles.logoContainer}>
            <img
              src="logotipo-fundamentos.png"
              style={styles.logo}
              alt="Fundamentos"
            />
          </div>

          <Text style={styles.instructions}>
            Configure a autenticação em duas etapas escaneando o QR code abaixo
            com um app como Google Authenticator ou Authy. Se preferir, digite a
            chave manualmente no app.
          </Text>

          {qrDataUri && (
            <Box style={styles.qrContainer}>
              <img
                src={qrDataUri}
                alt="QR code para autenticação em duas etapas"
                style={styles.qrImage}
                data-testid="totp-qr-code"
              />
            </Box>
          )}

          {manualKey && (
            <Text style={styles.manualKey} data-testid="totp-manual-key">
              Chave manual: <strong>{manualKey}</strong>
            </Text>
          )}

          <TextField
            variant="outlined"
            size="small"
            label="Código de confirmação"
            style={styles.loginField}
            value={code}
            name="code"
            onChange={handleCodeChange}
            onPaste={handleCodePaste}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 6,
              "data-testid": "totp-setup-code-input",
            }}
            autoFocus
          />

          <Button
            style={styles.loginButton}
            variant="contained"
            color="primary"
            size="medium"
            type="submit"
          >
            Confirmar e ativar 2FA
          </Button>

          <div style={styles.buttonsContainer}>
            <Button
              color="primary"
              style={{ color: "#000" }}
              size="small"
              onClick={redirectToLogin}
            >
              Voltar para login
            </Button>
          </div>
        </Paper>
      </form>

      {showSnackBar && (
        <CustomSnackBar
          severity={statusMessage.severity}
          message={statusMessage.message}
          open={showSnackBar}
          handleClose={() => setShowSnackBar(false)}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  loginBox: {
    padding: 20,
    margin: 10,
    maxWidth: 360,
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    margin: 25,
  },
  logo: {
    maxWidth: 150,
  },
  instructions: {
    marginBottom: 16,
    lineHeight: 1.5,
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 16,
  },
  qrImage: {
    width: 220,
    height: 220,
    maxWidth: "100%",
  },
  manualKey: {
    marginBottom: 12,
    wordBreak: "break-all",
    textAlign: "center",
  },
  loginField: {
    marginTop: 10,
  },
  loginButton: {
    width: "100%",
    height: 35,
    marginTop: 20,
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 10,
  },
};
